import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../components/Icon";
import { useAuth } from "../context/AuthContext";

type AuthMode = "login" | "register";

export function LoginPage() {
  const { status, error: authError, login, register } = useAuth();
  const [mode, setMode] = useState<AuthMode>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  if (status === "authenticated") return <Navigate to="/overview" replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (username.trim().length < 2) return setMessage("用户名至少需要 2 个字符");
    if (password.length < 6) return setMessage("密码至少需要 6 个字符");
    setBusy(true);
    setMessage("");
    try {
      if (mode === "login") await login(username.trim(), password);
      else await register(username.trim(), password);
      const state = location.state as { from?: string } | null;
      navigate(state?.from || "/overview", { replace: true });
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "身份验证失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <div className="login-atmosphere" aria-hidden="true"><span /><span /><span /></div>
      <section className="login-story">
        <div className="brand-lockup brand-lockup--login">
          <span className="brand-mark brand-mark--light"><span /></span>
          <span><b>OA FLOW</b><small>REAUTH OPERATIONS</small></span>
        </div>
        <div className="login-story__copy">
          <span className="eyebrow eyebrow--light">重新授权，不再盯着日志等待</span>
          <h1>从账号准备到结果回填，<em>始终知道下一步。</em></h1>
          <p>任务会在服务端继续运行。重新打开页面，进度、失败原因和积分变化仍可恢复。</p>
        </div>
        <div className="login-story__flow" aria-label="工作流程">
          <span><b>01</b>账号预检</span><i />
          <span><b>02</b>授权执行</span><i />
          <span><b>03</b>结果回填</span>
        </div>
      </section>

      <section className="login-panel" aria-labelledby="auth-title">
        <div className="login-panel__inner">
          <span className="login-panel__index">SECURE WORKSPACE / 01</span>
          <h2 id="auth-title">{mode === "login" ? "回到工作空间" : "创建操作账号"}</h2>
          <p>{mode === "login" ? "登录后自动恢复最近任务。" : "注册成功后会直接进入工作台。"}</p>

          <div className="auth-switch" role="tablist" aria-label="身份入口">
            <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "is-active" : ""} onClick={() => { setMode("login"); setMessage(""); }}>登录</button>
            <button type="button" role="tab" aria-selected={mode === "register"} className={mode === "register" ? "is-active" : ""} onClick={() => { setMode("register"); setMessage(""); }}>注册</button>
          </div>

          {authError ? <div className="auth-service-error" role="status"><Icon name="alert" size={18} />身份服务暂不可用：{authError}</div> : null}

          <form onSubmit={submit} className="auth-form">
            <label htmlFor="auth-username">用户名</label>
            <div className="input-shell">
              <Icon name="user" size={19} />
              <input id="auth-username" name="username" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="输入用户名" disabled={busy} />
            </div>
            <label htmlFor="auth-password">密码</label>
            <div className="input-shell">
              <Icon name="key" size={19} />
              <input id="auth-password" name="password" type={showPassword ? "text" : "password"} autoComplete={mode === "register" ? "new-password" : "current-password"} minLength={6} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 6 位" disabled={busy} />
              <button type="button" className="input-shell__action" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "隐藏密码" : "显示密码"}>
                <Icon name={showPassword ? "eye-off" : "eye"} size={19} />
              </button>
            </div>
            <div className="form-message" aria-live="polite">{message}</div>
            <button className="button button--primary button--wide" disabled={busy}>
              {busy ? <span className="button-spinner" /> : null}
              {busy ? "正在验证" : mode === "login" ? "进入工作台" : "注册并进入"}
              {!busy ? <Icon name="arrow" size={18} /> : null}
            </button>
          </form>
          <p className="auth-footnote">会话使用同源安全 Cookie，不在浏览器保存密码。</p>
        </div>
      </section>
    </main>
  );
}
